<template>
  <ig-page-base>
    <div class="box attributes__content">
      <div class="attributes__title-box">
        <h2 class="title attributes__title has-text-weight-semibold">
          {{ $t('storePages.product.attributes.title') }}
        </h2>

        <b-button
          type="is-success"
          tag="router-link"
          :to="{ name: 'manage-attribute' }"
          :label="$t('storePages.product.attributes.btnNewAttribute')"
        ></b-button>
      </div>

      <div class="attributes__filters">
        <div class="columns">
          <div class="column is-6">
            <form @submit.prevent="getAttributesList()">
              <b-field grouped>
                <b-input
                  v-model="filters.query"
                  :placeholder="$t('storePages.product.attributes.filters.query')"
                  expanded />
                <p class="control">
                  <b-button
                    native-type="submit"
                    type="is-primary"
                    :label="$t('storePages.product.attributes.filters.btnSearch')"
                    :loading="loading"
                    :disabled="loading"
                  ></b-button>
                </p>
              </b-field>
            </form>
          </div>
          <div class="column has-text-right">
            <b-button
              type="is-primary"
              icon-left="filter"
              outlined
              :label="$t('storePages.product.attributes.filters.btnFilters')"
              :disabled="loading"
              @click="showFilters = true"
            ></b-button>

            <ig-sidebar-filters
              v-model="showFilters"
              v-slot="{ filters }"
              @submit="applyFilters"
            >
              <b-field
                :label="$t('storePages.product.attributes.filters.type.label')"
                label-position="inside">

                <b-select
                  v-model="filters.type"
                  :placeholder="$t('storePages.product.attributes.typeEnum.ALL')"
                  expanded>
                  <option :value="undefined">
                    {{ $t('storePages.product.attributes.typeEnum.ALL') }}
                  </option>
                  <option value="COMBOBOX">
                    {{ $t('storePages.product.attributes.typeEnum.COMBOBOX') }}
                  </option>
                  <option value="TEXT">
                    {{ $t('storePages.product.attributes.typeEnum.TEXT') }}
                  </option>
                </b-select>
              </b-field>
            </ig-sidebar-filters>
          </div>
        </div>
      </div>

      <hr />

      <b-table
        :loading="loading"
        :data="attributes"
        backend-sorting
        default-sort="name"
        default-sort-direction="desc"
        paginated
        pagination-simple
        backend-pagination
        @page-change="onPageChange"
        :total="total"
        :per-page="filters.limit"
        :current-page.sync="filters.page"
        @sort="sort"
        @click="row => editAttribute(row.id)"
        striped
        hoverable
        scrollable
        :row-class="() => 'attributes__table-row'"
      >
        <template #empty>
          <ig-empty />
        </template>

        <template #bottom-left>
          <ig-per-page
            :total-displayed="attributes.length"
            :total="total"
            :perPage="filters.limit"
            @change="changePerPage"
          />
        </template>

        <b-table-column
          field="name"
          :label="$t('storePages.product.attributes.table.header.attribute')"
          v-slot="props"
          sortable
        >
          <p>
            {{ props.row.name }}
            <b-tag
              type="is-success is-light"
              rounded
              v-if="props.row.isDefault"
            >
              {{ $t('storePages.product.attributes.table.data.default') }}
            </b-tag>
          </p>
          <div class="is-size-7" v-if="props.row.variations && props.row.variations.length > 0">
            <b-taglist>
              <b-tag
                v-for="variation in props.row.variations"
                :key="variation.id"
                type="is-info is-light"
                rounded>{{ variation.value }}</b-tag>
            </b-taglist>
          </div>
        </b-table-column>

        <b-table-column
          field="name"
          :label="$t('storePages.product.attributes.table.header.code')"
          v-slot="props"
          sortable
        >
          {{ props.row.code }}
        </b-table-column>

        <b-table-column
          field="type"
          :label="$t('storePages.product.attributes.table.header.type')"
          centered
          sortable
          v-slot="props"
        >
          <span class="tag is-uppercase" :class="{
          'is-success': props.row.type === 'COMBOBOX',
          'is-primary':  props.row.type === 'TEXT',
          }">
            {{ $t(`storePages.product.attributes.typeEnum.${props.row.type}`) }}
          </span>
        </b-table-column>

        <b-table-column
          field="type"
          :label="$t('storePages.product.attributes.table.header.required')"
          centered
          sortable
          v-slot="props"
        >
          <span class="tag is-uppercase" :class="{
          'is-success': props.row.isRequired,
          'is-danger': !props.row.isRequired,
          }">
            {{ $t(`storePages.product.attributes.table.data.required.${props.row.isRequired}`) }}
          </span>
        </b-table-column>

        <b-table-column
          width="100"
          v-slot="props"
          custom-key="actions"
        >
          <div class="buttons">
            <b-tooltip
              append-to-body
              :label="$t('storePages.product.attributes.table.data.actions.edit')">
              <b-button
                class="categories__btn-action"
                type="is-default is-light"
                icon-left="pen"
                size="is-small"
                @click.stop="editAttribute(props.row.id)">
              </b-button>
            </b-tooltip>
            <b-tooltip
              append-to-body
              :label="$t('storePages.product.attributes.table.data.actions.delete')">
              <b-button
                class="categories__btn-action"
                type="is-danger is-light"
                icon-left="trash"
                size="is-small"
                @click.stop="deleteAttribute(props.row)">
              </b-button>
            </b-tooltip>
          </div>
        </b-table-column>
      </b-table>
    </div>
  </ig-page-base>
</template>

<script>
import Vue from 'vue';
import { createNamespacedHelpers } from 'vuex';
import {
  IgPageBase,
  IgEmpty,
  IgSidebarFilters,
  IgPerPage, EventBus,
} from '@integrai/ui';

const { mapState, mapActions } = createNamespacedHelpers('attributes');

export default Vue.extend({
  name: 'page-attributes',
  components: {
    IgPageBase,
    IgEmpty,
    IgSidebarFilters,
    IgPerPage,
  },
  data() {
    return {
      showFilters: false,
      filters: {
        limit: 10,
        page: 1,
      },
    };
  },
  computed: {
    ...mapState([
      'loading',
      'attributes',
      'total',
    ]),
  },
  methods: {
    ...mapActions([
      'getAttributes',
    ]),
    sort(sortField, sortOrder) {
      Object.assign(this.filters, {
        sortField,
        sortOrder,
      });

      this.getAttributesList();
    },
    onPageChange(page) {
      Object.assign(this.filters, {
        page,
      });

      this.getAttributesList();
    },
    changePerPage(limit) {
      Object.assign(this.filters, {
        limit,
      });

      this.getAttributesList();
    },
    applyFilters(filters) {
      this.getAttributesList({
        ...this.filters,
        ...filters || {},
      });
    },
    async getAttributesList(filters) {
      await this.getAttributes(filters || this.filters);
    },
    editAttribute(id) {
      return this.$router.push({
        name: 'manage-attribute',
        params: {
          id,
        },
      });
    },
    deleteAttribute(row) {
      return this.$buefy.dialog.confirm({
        title: String(this.$t('storePages.product.attributes.deleteDialog.title')),
        message: String(this.$t('storePages.product.attributes.deleteDialog.message', {
          name: row.name,
        })),
        confirmText: String(this.$t('storePages.product.attributes.deleteDialog.confirm')),
        cancelText: String(this.$t('storePages.product.attributes.deleteDialog.cancel')),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: async () => {
          await this.removeCategory(row.id);
          await this.getCategories();

          EventBus.$emit('toast', {
            type: 'is-success',
            message: this.$t('storePages.product.attributes.deleteDialog.successMessage'),
          });
        },
      });
    },
  },
  async beforeMount() {
    await this.getAttributesList();
  },
});
</script>

<style lang="scss">
  .attributes{
    &__title-box{
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
    }
    &__title{
      padding-bottom: 1rem;
    }
    &__table-row{
      cursor: pointer;
      .categories__btn-action{
        visibility: hidden;
      }
      &:hover{
        .categories__btn-action{
          visibility: visible;
        }
      }
    }
  }
</style>
